import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BackLink } from "../ui/BackLink";
import { Avatar } from "../ui/Avatar";
import {
  givingTotal,
  useTagDefs,
  useTopDonors,
  useUnattributedDonations,
  type UnattributedDonation,
} from "../data";
import { attributeDonation } from "../actions";
import { PersonPicker } from "../ui/PersonPicker";
import { IcAlert, IcChevron, IcSearch, IcWallet } from "../ui/icons";

export function DonorsPage() {
  const [tagId, setTagId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { data: tags } = useTagDefs("person");
  const { data, loading, error } = useTopDonors(tagId);
  const orphans = useUnattributedDonations();

  const visible = useMemo(() => tags.filter((t) => t.status !== "hidden"), [tags]);
  const activeName = tagId ? (visible.find((t) => t.id === tagId)?.name ?? "") : null;

  const shown = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return data.filter((p) => {
      if (p.isDemo) return false;
      if (!q) return true;
      return (p.displayName ?? "").toLowerCase().includes(q);
    });
  }, [data, searchTerm]);
  const sum = shown.reduce((acc, p) => acc + givingTotal(p), 0);

  return (
    <div className="screen">
      <header className="hdr">
        <BackLink fallback="/people" label="חזרה" />
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>
              <IcWallet size={19} /> תורמים
            </h1>
            <p className="sub">
              {loading
                ? "טוענים…"
                : shown.length === 0
                  ? activeName
                    ? `אין תרומות רשומות בתגית ${activeName}`
                    : "אין עדיין תרומות רשומות"
                  : `${shown.length} תורמים · ${sum.toLocaleString("he-IL", { maximumFractionDigits: 0 })} ₪`}
            </p>
          </div>
        </div>

        <div className="search-wrap" style={{ marginTop: 8 }}>
          <span className="search-ico" aria-hidden="true">
            <IcSearch size={16} />
          </span>
          <input
            className="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="סינון תורמים לפי שם…"
            autoComplete="off"
          />
          {searchTerm && (
            <button
              type="button"
              className="search-clear"
              onClick={() => setSearchTerm("")}
              aria-label="ניקוי חיפוש"
            >
              ×
            </button>
          )}
        </div>

        {/* The classification filter. Horizontally scrollable rather than
            wrapped: with a couple of dozen tags a wrapped row pushes the list
            itself off a phone screen. */}
        <div className="pilltabs" role="tablist" style={{ overflowX: "auto" }}>
          <button
            role="tab"
            aria-selected={tagId === null}
            className={tagId === null ? "on" : undefined}
            onClick={() => setTagId(null)}
          >
            הכול
          </button>
          {visible.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tagId === t.id}
              className={tagId === t.id ? "on" : undefined}
              onClick={() => setTagId(t.id)}
            >
              {t.name}
            </button>
          ))}
        </div>
      </header>

      <div className="body">
        {/* Only when there is something to fix — an empty warning is noise. */}
        {orphans.data.length > 0 && <UnattributedCard rows={orphans.data} />}

        {error && (
          <div className="notice error">
            לא הצלחנו לטעון את התורמים. זו תקלה, לא סימן שאין תורמים.
            <br />
            <span className="faint faint-xs mono">
              {error}
            </span>
          </div>
        )}

        {!loading && shown.length === 0 && !error && (
          <div className="empty">
            <div className="empty-ico tint-amber">
              <IcWallet size={26} />
            </div>
            <h3>{activeName ? `אין תרומות בתגית ${activeName}` : "אין תרומות רשומות"}</h3>
            <p>
              הסכומים מתעדכנים כל לילה. תרומה שנרשמה היום תופיע כאן מחר, או מיד אחרי
              חישוב מחדש בהגדרות.
            </p>
          </div>
        )}

        {shown.length > 0 && (
          <div className="card">
            {shown.map((p, i) => {
              const verified = (p.donationTotalVerifiedMinor ?? 0) / 100;
              const imported = (p.donationTotalImportedMinor ?? 0) / 100;
              return (
                <Link
                  to={`/people/${p.id}`}
                  key={p.id}
                  className="list-row"
                  style={{ borderBottom: i === shown.length - 1 ? "0" : undefined }}
                >
                  {/* The position, because "who is third" is the question this
                      screen exists to answer. */}
                  <span className="donor-rank">{i + 1}</span>
                  <Avatar name={p.displayName ?? "ללא שם"} size={38} />
                  <span className="grow">
                    <span className="name-row">
                      {p.displayName ?? "ללא שם"}
                    </span>
                    <span className="muted" style={{ display: "block" }}>
                      {givingTotal(p).toLocaleString("he-IL", { maximumFractionDigits: 0 })} ₪
                      {typeof p.donationCount === "number" && p.donationCount > 0
                        ? ` · ${p.donationCount} תרומות`
                        : ""}
                    </span>
                    {/* The split, so the ranking total is never mistaken for
                        money a receipt could be issued against. */}
                    {verified > 0 && imported > 0 && (
                      <span className="faint" style={{ display: "block" }}>
                        {verified.toLocaleString("he-IL", { maximumFractionDigits: 0 })} ₪ מאומתים ·{" "}
                        {imported.toLocaleString("he-IL", { maximumFractionDigits: 0 })} ₪ מייבוא
                      </span>
                    )}
                  </span>
                  <IcChevron size={16} />
                </Link>
              );
            })}
          </div>
        )}

        {shown.length >= 50 && (
          <p className="faint" style={{ padding: "8px 6px 0", textAlign: "center" }}>
            מוצגים 50 התורמים הגדולים. בחרו תגית כדי לצמצם את הרשימה.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Donations with no donor, and the two taps that fix one.
 *
 * Each row shows what is actually known about the money — amount, what it was
 * for, when, and why it lost its owner — because that is all anybody has to go
 * on when deciding whose it was. Attaching does not mark anything verified: it
 * says who gave it, not that a payment provider confirmed it.
 */
function UnattributedCard({ rows }: { rows: UnattributedDonation[] }) {
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState<Set<string>>(new Set());

  const left = rows.filter((r) => !done.has(r.id));
  const total = left.reduce((a, r) => a + (r.amountMinor ?? 0), 0);

  if (left.length === 0) return null;

  return (
    <section className="section">
      <div className="card card-highlight">
        <button
          className="list-row"
          style={{ borderBottom: "0", width: "100%", textAlign: "start" }}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="rail-ico tint-red">
            <IcAlert size={17} />
          </span>
          <span className="grow">
            <span className="name-row">
              {left.length} תרומות בלי שם
            </span>
            <span className="muted">
              {(total / 100).toLocaleString("he-IL")} ₪ שלא משויכים לאף אחד ברשימה
            </span>
          </span>
          <span
            style={{
              color: "var(--ink-3)",
              flexShrink: 0,
              transform: open ? "rotate(-90deg)" : undefined,
            }}
          >
            <IcChevron size={17} />
          </span>
        </button>

        {open && (
          <div>
            {left.map((d) => (
              <OrphanRow key={d.id} donation={d} onAttached={() => setDone((s) => new Set(s).add(d.id))} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/** Plain Hebrew for how a donation came to have no owner. */
const ORPHAN_REASON: Record<string, string> = {
  unlinked_at_import: "בייבוא לא הצלחנו לזהות את מי שהשורה מדברת עליו",
  donor_person_deleted: "הכרטיס של התורם נמחק אחרי שהתרומה נרשמה",
};

function OrphanRow({
  donation,
  onAttached,
}: {
  donation: UnattributedDonation;
  onAttached: () => void;
}) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reason = (donation.reviewReasons ?? [])
    .map((r) => ORPHAN_REASON[r])
    .filter(Boolean)
    .join(" · ");

  async function attach(personId: string) {
    setBusy(true);
    setErr(null);
    try {
      await attributeDonation(donation.id, personId);
      onAttached();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "לא הצלחנו לשייך. נסו שוב.");
      setBusy(false);
    }
  }

  return (
    <div style={{ borderTop: "1px solid var(--line)", padding: "10px 14px 12px" }}>
      <p className="name-row">
        {((donation.amountMinor ?? 0) / 100).toLocaleString("he-IL")} ₪
        {donation.purpose ? " · " + donation.purpose : ""}
      </p>
      <p className="faint" style={{ margin: "2px 0 0" }}>
        {[
          donation.collectedAt
            ? new Date(donation.collectedAt).toLocaleDateString("he-IL", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            : "בלי תאריך",
          reason || null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>

      {picking ? (
        <div style={{ marginTop: 8 }}>
          <PersonPicker
            personId={null}
            personName=""
            disabled={busy}
            placeholder="חיפוש לפי שם או טלפון…"
            onPick={(person) => person && void attach(person.id)}
          />
          <button className="btn" style={{ marginTop: 6 }} disabled={busy} onClick={() => setPicking(false)}>
            ביטול
          </button>
        </div>
      ) : (
        <div className="btn-row" style={{ marginTop: 8 }}>
          <button className="btn" disabled={busy} onClick={() => setPicking(true)}>
            {busy ? "משייכים…" : "שייכו לאיש קשר"}
          </button>
        </div>
      )}

      {err && (
        <p className="faint danger" style={{ margin: "6px 0 0" }}>
          {err}
        </p>
      )}
    </div>
  );
}
